import { useState } from 'react';
import type { Channel } from '@comms/shared';
import { notifyLevel, useStore } from '../store';
import { api } from '../lib/api';
import { useIsMobile } from '../hooks/mobile';
import { Icon, cx } from './ui';

type Level = 'all' | 'mentions' | 'nothing';
const LABEL: Record<Level, string> = {
  all: 'All messages',
  mentions: 'Only @mentions',
  nothing: 'Nothing',
};

/**
 * The bell in a channel's header: choose how this channel (or thread) notifies you,
 * overriding the server's setting, or mute it. Applies to in-app and push notifications.
 */
export function ChannelNotifyButton({ channel }: { channel: Channel }) {
  const [open, setOpen] = useState(false);
  const mobile = useIsMobile();
  const own = useStore((s) => s.notificationSettings[channel.id]);
  const effective = useStore((s) => notifyLevel(s, channel.id));
  const inherited = useStore((s) => notifyLevel(s, channel.id, true));
  const where = channel.threadMetadata ? 'channel' : 'server';
  const muted = effective === 'muted';

  const set = (body: { level?: Level | null; muted?: boolean }) =>
    void api('PUT', `/users/@me/notification-settings/${channel.id}`, body)
      .catch(() => {})
      .finally(() => setOpen(false));

  const option = (label: string, checked: boolean, onClick: () => void, sub?: string) => (
    <button
      key={label}
      type="button"
      role="menuitemradio"
      aria-checked={checked}
      onClick={onClick}
      className="flex w-full items-center gap-2 rounded px-3 py-2 text-left text-sm text-text-1 hover:bg-bg-hover"
    >
      <span className="w-4 shrink-0 text-accent">{checked ? '✓' : ''}</span>
      <span className="min-w-0 flex-1">
        {label}
        {sub && <span className="block text-xs text-text-3">{sub}</span>}
      </span>
    </button>
  );

  const list = (
    <div role="menu" aria-label="Notification settings">
      {option(
        'Default',
        !own?.level,
        () => set({ level: null }),
        inherited === 'muted'
          ? `The ${where} is muted`
          : `Follow the ${where}: ${LABEL[inherited as Level]}`,
      )}
      {(['all', 'mentions', 'nothing'] as const).map((l) =>
        option(LABEL[l], own?.level === l, () => set({ level: l })),
      )}
      <div className="my-1 h-px bg-bg-3" />
      {option(own?.muted ? 'Unmute channel' : 'Mute channel', false, () =>
        set({ muted: !own?.muted }),
      )}
    </div>
  );

  return (
    <div className="relative">
      <button
        type="button"
        title={muted ? 'Notifications (muted)' : 'Notification settings'}
        aria-label="Notification settings"
        aria-expanded={open}
        onClick={() => setOpen(!open)}
        className={cx(
          'hover:text-text-0',
          open ? 'text-text-0' : 'text-text-2',
          muted && 'opacity-50',
        )}
      >
        <Icon name="bell" />
      </button>
      {open &&
        (mobile ? (
          <div
            className="fixed inset-0 z-50 flex items-end bg-black/50"
            onClick={() => setOpen(false)}
          >
            <div
              className="safe-bottom w-full rounded-t-2xl bg-bg-1 p-2 shadow-2xl"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="mx-auto mb-2 h-1 w-10 rounded-full bg-bg-3" />
              {list}
            </div>
          </div>
        ) : (
          <>
            <div aria-hidden className="fixed inset-0 z-20" onClick={() => setOpen(false)} />
            <div className="absolute right-0 top-full z-30 mt-2 w-64 rounded bg-bg-0 p-1.5 shadow-xl">
              {list}
            </div>
          </>
        ))}
    </div>
  );
}
